import { Edit, MoreVert } from '@mui/icons-material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { IconButton, Menu } from '@mui/material'
import { CellContext } from '@tanstack/react-table'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { ListItemButton } from 'components/List'
import { EndAbTestDialog } from 'features/ab-tests/components/EndAbTestDialog'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useMenu } from 'hooks/useMenu'
import { useAbility } from 'providers/casl'
import { ROUTES } from 'routes'
import { AbTestsForTable } from '../../hooks/useGetAbTests/types'
import { DeleteDialog } from './components/DeleteDialog'
import { DeleteItem } from './components/DeleteItem'
import { EndTestItem } from './components/EndTestItem'
import { StartTestItem } from './components/StartTestItem'

export const ActionsCell = ({
  row: { original: abTest },
}: CellContext<AbTestsForTable, unknown>): JSX.Element => {
  const { domainName, id, status, usedVariantsData } = abTest
  const ability = useAbility()
  const { workspaceId } = useWorkspaceParam()
  const { t } = useTranslation('features/abTests')
  const { t: tC } = useTranslation('common')
  const { anchor, close, isOpen, open } = useMenu()
  const navigate = useNavigate()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isEndTestDialogOpen, setIsEndTestDialogOpen] = useState(false)

  const handleOpenDeleteDialog = (): void => {
    close()
    setIsDeleteDialogOpen(true)
  }

  const handleOpenEndTestDialog = (): void => {
    close()
    setIsEndTestDialogOpen(true)
  }

  const EditItem = (): JSX.Element => (
    <ListItemButton
      data-cy='actions-edit'
      icon={
        status === 'draft' ? (
          <Edit data-cy='edit-icon' />
        ) : (
          <VisibilityIcon data-cy='visibility-icon' />
        )
      }
      onClick={(): void =>
        navigate(
          generatePath(ROUTES.AB_TESTS.EDIT, {
            id,
            workspaceId,
          }),
        )
      }
      text={status === 'draft' ? tC('form.action.edit') : t('list.actions.details')}
    />
  )

  const showStartTestItem: boolean = status === 'draft' && ability.can('update', 'AbTests')
  const showEndTestItem: boolean = status === 'active' && ability.can('update', 'AbTests')
  const showEditItem: boolean = ability.can('access', 'AbTestsPage-edit')
  const showDeleteItem: boolean = status === 'draft' && ability.can('delete', 'AbTests')

  return (
    <>
      <IconButton onClick={open}>
        <MoreVert data-cy='more-vert-icon' />
      </IconButton>

      <Menu
        anchorEl={anchor}
        onClose={close}
        open={isOpen}
      >
        {showStartTestItem && <StartTestItem id={id} />}
        {showEndTestItem && <EndTestItem setDialogOpen={handleOpenEndTestDialog} />}
        {showEditItem && <EditItem />}
        {showDeleteItem && <DeleteItem setDialogOpen={handleOpenDeleteDialog} />}
      </Menu>

      <EndAbTestDialog
        abTest={{
          domain: { name: domainName },
          id,
          usedVariantsData,
        }}
        isOpen={isEndTestDialogOpen}
        setOpen={setIsEndTestDialogOpen}
      />

      <DeleteDialog
        id={id}
        isOpen={isDeleteDialogOpen}
        setOpen={setIsDeleteDialogOpen}
      />
    </>
  )
}
